import styled from 'types/theme.types';
import RadioGroup from 'components/common/radio-group';
import { INFINITE_VALUE, INHERIT_FROM_ORG } from 'constants/ttl.constants';
import { FormattedMessage } from 'react-intl';
import type { UseFormReturnType } from 'hooks/use-form.hook';
import type { TtlPolicy } from 'components/policies/common-ttl';
import { INIT_TTL_VALUE } from 'components/policies/common-ttl';

export type TtlType = 'max' | 'default';
export interface TtlRadioButtonsProps {
  form: UseFormReturnType<TtlPolicy>;
  name: 'maxTtl' | 'defaultTtl';
}

export const TtlRadioButtons: React.FC<TtlRadioButtonsProps> = ({ form, name }) => {
  const canSetDefaultToInfinite: boolean = !(name === 'defaultTtl' && form.getValues('maxTtl') !== INFINITE_VALUE);
  const applyChange = (selected: string): void => {
    if (selected === 'infinite') {
      // in case max ttl is not infinite just ignore when trying to switch default TTL to infinite (DefaultTTl <= MaxTTL)
      if (canSetDefaultToInfinite) form.setValue(name, INFINITE_VALUE);
    } else {
      form.setValue(name, INIT_TTL_VALUE);
    }
  };

  const ttlValue = form.getValues(name);

  return (
    <>
      <RadioButtonTitle>
        <FormattedMessage id={`project.settings.policies.ttl.radio.buttons.${name}.title`} />
      </RadioButtonTitle>
      <RadioGroup
        horizontal
        value={ttlValue === INFINITE_VALUE ? 'infinite' : 'custom'}
        onChange={applyChange}
        disabled={form.isSubmitting || ttlValue === INHERIT_FROM_ORG}
        items={[
          {
            value: 'infinite',
            'data-e2e': `${name}-infinite-radio-button`,
            content: <FormattedMessage id={`project.settings.policies.ttl.radio.buttons.${name}.value`} />
          },
          {
            value: 'custom',
            'data-e2e': `${name}-number-radio-button`,
            content: <FormattedMessage id={'project.settings.policies.custom.ttl'} />
          }
        ]}
      />
    </>
  );
};

const RadioButtonTitle = styled.div`
  width: 7em;

  /**
   * The row is a flex container, so the title shrinks below its 7em basis and "Default TTL:" wraps onto
   * a second line — 64.9px of box for 84.5px of text. Only the shrink needs stopping.
   */

  flex-shrink: 0;
`;
